import { Injectable } from '@angular/core';
import { TimesheetIndexedDbStorageService } from './storage-timesheet.indexeddb.service';
import { TimesheetConcurrentStorageService } from './storage-timesheet.concurrent.service';
import { forkJoin, Observable, of } from 'rxjs';
import { TimesheetModel } from '../../models/low-level/timesheet.model';
import { mergeMap } from 'rxjs/operators';
import { TIMESHEET_KEYS } from './timesheet.enum';

@Injectable({ providedIn: 'root' })
export class StorageTimesheetService {
    public constructor(
        private readonly localTimesheetStorage: TimesheetIndexedDbStorageService,
        private readonly concurrentTimesheetStorage: TimesheetConcurrentStorageService,
    ) {}

    public getCurrentTimesheet(): Observable<TimesheetModel> {
        return this.getTimesheet(TIMESHEET_KEYS.CURRENT_TIMESHEET);
    }

    public saveCurrentTimesheetToLocal(timesheet: TimesheetModel): Observable<TimesheetModel> {
        // When only storing locally, don't update the `saveTimestamp` of the timesheet.
        // This timestamp should only be updated when also synched with the remote Concurrency Store
        return this.localTimesheetStorage.saveTimesheet(TIMESHEET_KEYS.CURRENT_TIMESHEET, timesheet);
    }

    public saveCurrentTimesheetToConcurrentStore(timesheet: TimesheetModel): Observable<TimesheetModel> {
        // Updating timestamp of last sync to remote and local store.
        timesheet.saveTimestamp = Date.now();
        return this.saveTimesheetToConcurrentStore(TIMESHEET_KEYS.CURRENT_TIMESHEET, timesheet);
    }

    public getSubmittedTimesheet(): Observable<TimesheetModel> {
        return this.getTimesheet(TIMESHEET_KEYS.SUBMITTED_TIMESHEET);
    }

    public saveSubmittedTimesheet(timesheet: TimesheetModel): Observable<TimesheetModel> {
        timesheet.saveTimestamp = Date.now();
        return this.saveTimesheetToConcurrentStore(TIMESHEET_KEYS.SUBMITTED_TIMESHEET, timesheet);
    }

    private getTimesheet(key: TIMESHEET_KEYS): Observable<TimesheetModel> {
        return forkJoin([
            this.concurrentTimesheetStorage.getTimesheet(key),
            this.localTimesheetStorage.getTimesheet(key),
        ]).pipe(
            mergeMap(([remoteTimesheet, localTimesheet]) => {
                if (remoteTimesheet && remoteTimesheet.saveTimestamp > localTimesheet.saveTimestamp) {
                    return this.localTimesheetStorage.saveTimesheet(key, remoteTimesheet);
                }
                return of(localTimesheet);
            }),
        );
    }

    private saveTimesheetToConcurrentStore(key: TIMESHEET_KEYS, timesheet: TimesheetModel): Observable<TimesheetModel> {
        return this.concurrentTimesheetStorage
            .saveData(key, timesheet)
            .pipe(mergeMap((ts) => this.localTimesheetStorage.saveTimesheet(key, ts)));
    }
}
